import React, { useEffect, useState } from 'react';

// One severity scale for everything: 0 = relaxed, 1 = at the limit, >1 = beyond it.
// Lines/trafos: loading / 100. Buses: 1 - margin to the nearest voltage limit.
const GREEN = [34, 197, 94];
const AMBER = [245, 158, 11];
const RED = [239, 68, 68];
const OVER = [190, 18, 60];
const NO_RESULT = [120, 110, 140];

const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));

export function severityColor(sev) {
  if (sev == null || Number.isNaN(sev)) return NO_RESULT;
  if (sev > 1) return OVER;
  if (sev <= 0.5) return GREEN;
  if (sev <= 0.8) return mix(GREEN, AMBER, (sev - 0.5) / 0.3);
  return mix(AMBER, RED, (sev - 0.8) / 0.2);
}

export const busSeverity = (r) => (r?.margin == null ? null : 1 - r.margin);
export const loadingSeverity = (r) => (r?.loading_percent == null ? null : r.loading_percent / 100);

export function indexResults(pf) {
  if (!pf) return null;
  const byId = (arr) => new Map((arr || []).map(r => [r.id, r]));
  return { bus: byId(pf.buses), line: byId(pf.lines), trafo: byId(pf.trafos), summary: pf.summary, scenario: pf.scenario };
}

export function describeResult(table, r) {
  if (!r) return null;
  if (table === 'bus') {
    if (r.vm_pu == null) return 'no voltage (not supplied)';
    const low = r.vm_pu - r.min_vm_pu < r.max_vm_pu - r.vm_pu;
    const limit = low ? r.min_vm_pu : r.max_vm_pu;
    const gap = Math.abs(r.vm_pu - limit) * 100;
    const beyond = r.margin < 0;
    return `${r.vm_pu.toFixed(3)} p.u. · ${beyond ? `${gap.toFixed(1)}% ${low ? 'below' : 'above'} limit`
      : `${gap.toFixed(1)}% from ${low ? 'lower' : 'upper'} limit`}`;
  }
  return r.loading_percent == null ? 'no loading result' : `${r.loading_percent.toFixed(0)}% loaded`;
}

// Timeline labels are computed client-side from the start + step size, in UTC
// so the browser's timezone never shifts them.
export function stepLabel(tl, step) {
  if (!tl || step == null) return '';
  const d = new Date(Date.parse(`${tl.start}Z`) + step * tl.step_hours * 3600e3);
  const p = (n) => String(n).padStart(2, '0');
  const date = `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
  return tl.step_hours >= 24 ? date : `${date} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

const SCENARIOS = [
  ['saved', 'As saved', 'The p_mw values stored in the net file'],
  ['peak_load', 'Peak load', 'The moment with the highest net demand of the profiled assets'],
  ['peak_pv', 'Peak PV', 'The moment with the highest net feed-in (generation minus load)'],
  ['step', 'Time step', 'Any moment of the profile timeline'],
];

function ScenarioPicker({ timeline, scenario, onScenario, info }) {
  // The slider moves freely; the power flow only re-runs once it rests.
  const [draft, setDraft] = useState(scenario.step ?? 0);
  useEffect(() => { setDraft(scenario.step ?? 0); }, [scenario.step]);
  useEffect(() => {
    if (scenario.kind !== 'step' || draft === scenario.step) return undefined;
    const t = setTimeout(() => onScenario({ kind: 'step', step: draft }), 300);
    return () => clearTimeout(t);
  }, [draft, scenario, onScenario]);

  const max = timeline ? timeline.length - 1 : 0;
  const choose = (kind) => {
    if (kind === 'step') onScenario({ kind, step: info?.step ?? scenario.step ?? timeline?.peak_load_step ?? 0 });
    else onScenario({ kind, step: null });
  };
  return (
    <div className="nl-pf-scenario">
      <div className="nl-segmented" role="radiogroup" aria-label="Power flow scenario">
        {SCENARIOS.map(([k, label, hint]) => (
          <button key={k} role="radio" aria-checked={scenario.kind === k} title={hint}
            className={scenario.kind === k ? 'active' : ''} disabled={k !== 'saved' && !timeline}
            onClick={() => choose(k)}>{label}</button>
        ))}
      </div>
      {!timeline && <div className="nl-pf-note">This grid has no profiles, so only the saved values are available.</div>}
      {timeline && scenario.kind === 'step' && (
        <div className="nl-pf-slider">
          <button className="nl-icon-btn" aria-label="Previous time step" disabled={draft <= 0}
            onClick={() => setDraft(d => Math.max(0, d - 1))}>‹</button>
          <input type="range" min={0} max={max} value={draft} aria-label="Time step"
            onChange={(e) => setDraft(Number(e.target.value))} />
          <button className="nl-icon-btn" aria-label="Next time step" disabled={draft >= max}
            onClick={() => setDraft(d => Math.min(max, d + 1))}>›</button>
        </div>
      )}
      {timeline && scenario.kind !== 'saved' && (
        <div className="nl-pf-note">
          <b>{stepLabel(timeline, scenario.kind === 'step' ? draft : info?.step)}</b> ({timeline.step})
          {timeline.fixed > 0 && ` · ${timeline.fixed} asset${timeline.fixed === 1 ? '' : 's'} without a profile use fixed p_mw`}
        </div>
      )}
    </div>
  );
}

const rgb = (c) => `rgb(${c.join(',')})`;
const RAMP = `linear-gradient(90deg, ${rgb(GREEN)} 0%, ${rgb(GREEN)} 50%, ${rgb(AMBER)} 80%, ${rgb(RED)} 100%)`;

export function PowerFlowLegend({ pf, running, error, timeline, scenario, onScenario }) {
  const s = pf?.summary;
  const v = s?.violations;
  const nViol = v ? v.bus + v.line + v.trafo : 0;
  return (
    <div className="nl-pf-legend">
      <div className="nl-pf-head">
        Power flow {running && <span className="nl-pf-running">calculating…</span>}
      </div>
      <ScenarioPicker timeline={timeline} scenario={scenario} onScenario={onScenario} info={pf?.scenario} />
      {error ? <div className="nl-pf-error">{error}</div> : (
        <>
          <div className="nl-pf-scale">
            <div className="nl-pf-label">Lines &amp; transformers: loading</div>
            <div className="nl-pf-bar"><i style={{ background: RAMP }} /><b style={{ background: rgb(OVER) }} /></div>
            <div className="nl-pf-ticks"><span>0%</span><span>50%</span><span>80%</span><span>100%</span><span>over</span></div>
          </div>
          <div className="nl-pf-scale">
            <div className="nl-pf-label">Buses: distance to voltage limit</div>
            <div className="nl-pf-bar"><i style={{ background: RAMP }} /><b style={{ background: rgb(OVER) }} /></div>
            <div className="nl-pf-ticks"><span>mid-band</span><span /><span /><span>at limit</span><span>beyond</span></div>
          </div>
          {s && (
            <div className="nl-pf-summary">
              {s.vm_min && <div>V min <b>{s.vm_min.vm_pu?.toFixed(3)}</b> · V max <b>{s.vm_max.vm_pu?.toFixed(3)}</b> p.u.</div>}
              {s.max_line && <div>Max line loading <b>{s.max_line.loading_percent?.toFixed(0)}%</b></div>}
              {s.max_trafo && <div>Max trafo loading <b>{s.max_trafo.loading_percent?.toFixed(0)}%</b></div>}
              <div className={nViol ? 'nl-pf-bad' : 'nl-pf-good'}>
                {nViol
                  ? `${nViol} limit violation${nViol === 1 ? '' : 's'} (${v.bus} bus, ${v.line} line, ${v.trafo} trafo)`
                  : 'No limit violations'}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
